// The chart's cycle-window band, built from a STORED window (DWLF-113 ①) —
// pure, so it is testable without rendering the chart.
//
// The band is [opens.time, closes.endTime]: the stored q25..q75 of the
// symbol's own inter-pivot gaps, folded against the real bar calendar by the
// writer. `hardMax` is the last bar by which the pivot must arrive or the
// window is a miss; it is drawn as a second, fainter box so the eye sees
// where "late" becomes "missed". A third box of the same weight runs from the
// EARLIEST gap ever seen to the band's opening edge, so the picture is
// symmetric: outside either faint box is unprecedented in both directions.
// That edge comes from the API as `earliest` (derived there by the library's
// one rule, DWLF-113 ③) — this file computes no edge of its own. Between the
// band and the faint boxes sits the OUTER band (DWLF-462): the 17th–83rd
// percentile of the same gaps, from the API's derived `bands.outer`, drawn
// lighter. A side with no width (its percentile ties with the band's) is
// simply not drawn. Every label carries
// the calibration state and the standing verification label — there is no
// window this chart presents as trustworthy while the DWLF-97 gate stands
// failed.
import { depthClaim, depthPositionWords, isConfirmedAnchor, isInZone } from './depthReading';

const ISO = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : '–');

const boundaryEnd = (b) => {
  if (!b) return null;
  if (Number.isFinite(b.endTime)) return b.endTime;
  return Number.isFinite(b.time) ? b.time : null;
};

/**
 * Where the window stands relative to `now` (and to a live pivot, if the
 * page has one inside it).
 *   'upcoming' | 'open' | 'overdue' (past closes, before hardMax) |
 *   'missed' (past hardMax) | 'landed' (a live pivot sits inside the band)
 */
export const cycleWindowPhase = ({ cycleWindow, now, livePivotTime }) => {
  const start = cycleWindow?.opens?.time;
  const end = boundaryEnd(cycleWindow?.closes);
  const hardEnd = boundaryEnd(cycleWindow?.hardMax);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (Number.isFinite(livePivotTime) && livePivotTime >= start && livePivotTime <= (hardEnd ?? end)) return 'landed';
  if (now < start) return 'upcoming';
  if (now <= end) return 'open';
  if (Number.isFinite(hardEnd) && now <= hardEnd) return 'overdue';
  return 'missed';
};

/**
 * Whether `now` is in the window's early zone: from the API's `earliest` edge (the shortest gap on record)
 * until the window opens: the indicators library's `earliest` window stage (last edge crossed). On the chart that stretch is the faint early-zone box plus, when the API sends an
 * outer band, its early side. False when the API sent no `earliest`.
 */
export const inEarlyZone = ({ cycleWindow, now }) => {
  const earliest = cycleWindow?.earliest?.time;
  const start = cycleWindow?.opens?.time;
  return Number.isFinite(earliest) && Number.isFinite(start) && now >= earliest && now < start;
};

const SIDE = {
  low: { code: 'CL', key: 'Next Cycle Low Window', fill: 'rgba(59, 130, 246, 0.18)', outer: 'rgba(59, 130, 246, 0.08)', anchor: 'stop' },
  high: { code: 'CH', key: 'Next Cycle High Window', fill: 'rgba(249, 115, 22, 0.18)', outer: 'rgba(249, 115, 22, 0.08)', anchor: 'target' },
};
const LATE_FILL = 'rgba(239, 68, 68, 0.22)';
// The two faint boxes either side of the band (early zone, hard max) share one weight.
const FAINT_FILL = 'rgba(148, 163, 184, 0.10)';

/** A stored window the chart can draw: finite opening and closing edges, in order. */
export const isDrawableWindow = (cycleWindow) => {
  const start = cycleWindow?.opens?.time;
  const end = boundaryEnd(cycleWindow?.closes);
  return Number.isFinite(start) && Number.isFinite(end) && end > start;
};

// 17 → "17th", 83 → "83rd": the percentile as said aloud.
const ordinal = (q) => {
  const n = Math.round(q * 100);
  const suffix = (n % 100 >= 11 && n % 100 <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th');
  return `${n}${suffix}`;
};

/**
 * The outer band's extent around a drawable window, from the API's derived `bands` (DWLF-462): `start` /
 * `end` are the band's own edges where that side has width, else the window's (`opens` / `closes` say
 * which: null = no width there, or a band the API could not date). `words` names the band ("17th–83rd
 * percentile"), `coreWords` the core ("25th–75th"), `lowBars` / `highBars` its bar range. Null when the
 * API sent no band — nothing is computed in its place.
 */
const outerBandExtent = (cycleWindow) => {
  const outer = cycleWindow?.bands?.outer;
  const core = cycleWindow?.bands?.core;
  if (!outer || !isDrawableWindow(cycleWindow)) return null;
  const start = cycleWindow.opens.time;
  const end = boundaryEnd(cycleWindow.closes);
  const lo = outer.opens && Number.isFinite(outer.opens.time) && outer.opens.time < start ? outer.opens : null;
  const hiEnd = boundaryEnd(outer.closes);
  const hi = Number.isFinite(hiEnd) && hiEnd > end ? outer.closes : null;
  return {
    start: lo ? lo.time : start,
    end: hi ? hiEnd : end,
    opens: lo,
    closes: hi,
    words: Number.isFinite(outer.lowerQuantile) && Number.isFinite(outer.upperQuantile)
      ? `${ordinal(outer.lowerQuantile)}–${ordinal(outer.upperQuantile)} percentile` : 'outer band',
    coreWords: Number.isFinite(core?.lowerQuantile) && Number.isFinite(core?.upperQuantile)
      ? `${ordinal(core.lowerQuantile)}–${ordinal(core.upperQuantile)}` : 'core',
    lowBars: outer.lowBars,
    highBars: outer.highBars,
  };
};

/**
 * Series entries for one side's stored window, or [] when there is no window
 * (the caller shows an explicit "no window" state — nothing is computed in
 * its place).
 */
export const buildCycleWindowBand = ({ cycleWindow, side, now, minLow, maxHigh, livePivotTime, unitLabel = 'w' }) => {
  const cfg = SIDE[side];
  if (!cfg || !isDrawableWindow(cycleWindow)) return [];
  const start = cycleWindow.opens.time;
  const end = boundaryEnd(cycleWindow.closes);
  if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) return [];

  const phase = cycleWindowPhase({ cycleWindow, now, livePivotTime });
  const extrapolated = cycleWindow.opens?.extrapolated === true || cycleWindow.closes?.extrapolated === true;
  const late = phase === 'overdue' || phase === 'missed';
  const n = cycleWindow.gaps?.n;
  const centreBars = cycleWindow.centre?.bars;
  const calib = cycleWindow.calibration === 'uncalibrated' ? 'uncalibrated' : 'calibrated';
  const verif = cycleWindow.verification?.state || 'unverified';
  const hint = [
    Number.isFinite(centreBars) ? `centre ~${Math.round(centreBars)}${unitLabel}` : null,
    Number.isFinite(n) ? `n ${n}` : null,
    calib === 'uncalibrated' ? 'uncalibrated' : null,
    verif,
  ].filter(Boolean).join(' · ');
  const head = phase === 'missed' ? `Missed ${cfg.code} window`
    : phase === 'overdue' ? `Overdue ${cfg.code} window`
      : `Next ${cfg.code} window`;
  const label = `${head} (${hint})`;

  const entry = (minLow + maxHigh) / 2;
  const stop = minLow;
  const target = maxHigh;
  const anchorV = cfg.anchor === 'stop' ? stop : target;

  const band = {
    key: cfg.key,
    type: 'position',
    data: [{
      start,
      end,
      entry,
      stop,
      target,
      // Over the pane's whole height in `rewardColor`, whatever the axis resolves to (a depth zone or band beyond
      // the bars stretches it); the chart reads no price of a full-height box (DWLF-459).
      fullHeight: true,
      messages: [{ t: start, v: anchorV, text: label }],
      rewardColor: late ? LATE_FILL : cfg.fill,
      bubbleColor: 'rgba(15, 23, 42, 0.9)',
      textColor: 'white',
      fontSize: 11,
      pointer: true,
      padding: 6,
    }],
    // Text says the stored bar DATES (what the screener and setup snapshot
    // say); the box is drawn to the closing bar's END so the bar sits inside.
    tooltipFormatter: () => `${cycleWindow.opens?.date || ISO(start)} → ${cycleWindow.closes?.date || ISO(end)}`
      + `${extrapolated ? ' (projected beyond the series)' : ''} (${calib}, ${verif})`,
  };

  const faint = ({ key, from, to, tooltip, fill = FAINT_FILL }) => ({
    key,
    type: 'position',
    data: [{
      start: from,
      end: to,
      entry,
      stop,
      target,
      fullHeight: true,
      messages: [],
      rewardColor: fill,
      bubbleColor: 'rgba(15, 23, 42, 0.9)',
      textColor: 'white',
      fontSize: 11,
      pointer: false,
      padding: 6,
    }],
    tooltipFormatter: () => tooltip,
  });
  const { before, after } = windowSideBoxes({ cycleWindow, cfg, faint, unitLabel, trust: `${calib}, ${verif}` });
  return [...before, band, ...after];
};

/**
 * The boxes either side of the live window's band, outward: the outer band (DWLF-462) where that side has
 * width, then the faint early zone / hard max, each starting where the outer band ends.
 */
const windowSideBoxes = ({ cycleWindow, cfg, faint, unitLabel, trust }) => {
  const start = cycleWindow.opens.time;
  const end = boundaryEnd(cycleWindow.closes);
  const outer = outerBandExtent(cycleWindow);
  const outerStart = outer ? outer.start : start;
  const outerEnd = outer ? outer.end : end;
  const barsWord = (bars) => (Number.isFinite(bars) ? `${bars}${unitLabel}` : '–');
  const outerWords = outer
    ? `the ${outer.words} of past cycle lengths (${barsWord(outer.lowBars)}–${barsWord(outer.highBars)}); the darker band is the ${outer.coreWords} (${trust})`
    : '';
  const before = [];
  const after = [];

  const earliest = cycleWindow.earliest;
  if (earliest && Number.isFinite(earliest.time) && earliest.time < outerStart) {
    before.push(faint({
      key: `${cfg.key} Early`,
      from: earliest.time,
      to: outerStart,
      tooltip: `early zone ${earliest.date || ISO(earliest.time)} → ${(outer?.opens ?? cycleWindow.opens)?.date || ISO(outerStart)} — the shortest gap on record (${barsWord(earliest.bars)}); a pivot before here is unprecedented`,
    }));
  }
  if (outer?.opens) {
    before.push(faint({
      key: `${cfg.key} Outer Early`, from: outerStart, to: start, fill: cfg.outer,
      tooltip: `outer band ${outer.opens.date || ISO(outerStart)} → ${cycleWindow.opens?.date || ISO(start)} — ${outerWords}`,
    }));
  }
  if (outer?.closes) {
    after.push(faint({
      key: `${cfg.key} Outer Late`, from: end, to: outerEnd, fill: cfg.outer,
      tooltip: `outer band ${cycleWindow.closes?.date || ISO(end)} → ${outer.closes.date || ISO(outerEnd)} — ${outerWords}`,
    }));
  }
  const hardEnd = boundaryEnd(cycleWindow.hardMax);
  if (Number.isFinite(hardEnd) && hardEnd > outerEnd) {
    after.push(faint({
      key: `${cfg.key} Hard Max`,
      from: outerEnd,
      to: hardEnd,
      tooltip: `hard max ${cycleWindow.hardMax?.date || ISO(hardEnd)} — the pivot must arrive by here or the window is a miss`,
    }));
  }
  return { before, after };
};

// ---------------------------------------------------------------------------
// The window's PRICE zone (DWLF-416): how far price has typically moved into
// this side's past pivots, as prices from the current cycle's anchor (the
// highest high after a low ruler, the lowest low after a high ruler). DERIVED
// by the API (`depth`, the library's one rule) — this file computes no band of
// its own, it only places the box. Historical context, not a target: the
// tooltip says so, and the window's own label already says `unverified`.
// ---------------------------------------------------------------------------
const ZONE = {
  low: { key: 'Next Cycle Low Price Zone', fill: 'rgba(59, 130, 246, 0.30)', tail: 'rgba(59, 130, 246, 0.14)', move: 'fall into a low', extreme: 'high' },
  high: { key: 'Next Cycle High Price Zone', fill: 'rgba(249, 115, 22, 0.30)', tail: 'rgba(249, 115, 22, 0.14)', move: 'rise into a high', extreme: 'low' },
};
const ZONE_FAINT = 'rgba(148, 163, 184, 0.14)';
const fmtPct = (v) => `${Math.round(v)}%`;
const fmtPrice = (v) => {
  if (!Number.isFinite(v)) return '–';
  return Math.abs(v) >= 1000 ? v.toFixed(0) : v.toPrecision(5).replace(/\.?0+$/, '');
};

/**
 * A window whose price zone the chart can draw: a drawable window, and a band with a finite zone, pct and n.
 * ONE predicate for the builder and the "not drawn" sentence, so the sentence can never fall silent on a
 * zone that is missing.
 */
export const hasDrawableZone = (cycleWindow) => {
  const depth = cycleWindow?.depth;
  const fields = ['min', 'p25', 'median', 'p75', 'max'];
  return isDrawableWindow(cycleWindow)
    && !!depth?.zone && !!depth?.pct && Number.isFinite(depth.n)
    && fields.every((k) => Number.isFinite(depth.zone[k]) && Number.isFinite(depth.pct[k]));
};

/** The zone's wording: the move, the anchor, n with thin/partial flags, and the API's own label. */
const zoneWords = (depth, cfg) => {
  const flags = `${depth.thin ? ', thin' : ''}${depth.partialHistory ? ', partial history' : ''}`;
  // Optional wording: a malformed anchor drops the clause, it never breaks the chart. A running extreme
  // (indicators 19) is the cycle's high or low SO FAR, not a confirmed pivot, and is said to be.
  // Only an anchor that says it is a confirmed pivot is worded as one (or a v1 anchor, which always was);
  // anything else is not dressed as a pivot.
  const a = depth.anchor;
  let anchor = '';
  if (Number.isFinite(a?.price)) {
    if (isConfirmedAnchor(a)) {
      anchor = ` from ${fmtPrice(a.price)} (${a.pivotDate || ISO(a.pivotTime)})`;
    } else {
      anchor = ` from the running ${cfg.extreme} ${fmtPrice(a.price)} (${a.barDate || ISO(a.barTime)}; not yet a confirmed cycle ${cfg.extreme})`;
    }
  }
  // The API's own label for the band, as the timing band shows the window's.
  const claim = depthClaim(depth);
  // Where the last close sits (the API's one placement rule): IN ZONE is the one to make loud (Andy).
  const position = depth.reading?.position;
  const reading = depthPositionWords(position);
  return { flags, claim, reading, inZone: isInZone(depth), what: `typical ${cfg.move}: ${fmtPct(depth.pct.p25)}–${fmtPct(depth.pct.p75)}${anchor}` };
};


/**
 * Series entries for one side's price zone over the window's opens→closes span, or [] when the API
 * served no zone (no anchor yet, no band, or `depth` not requested). The p25–p75 zone is drawn solid; the
 * min–max range as a faint box clipped to the visible prices (a 59% max fall would otherwise rescale the
 * whole chart), with the true figures in its tooltip.
 */
export const buildCycleDepthZone = ({ cycleWindow, side, minLow, maxHigh }) => {
  const cfg = ZONE[side];
  if (!cfg || !hasDrawableZone(cycleWindow)) return [];
  const { depth } = cycleWindow;
  const { zone } = depth;
  if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) return [];
  // The zone spans the whole window in time: the core (opens→closes, the timing p25–p75) strongest, and the
  // timing tails (earliest→opens, closes→hardMax) in the same price band, fainter — so where time and price
  // line up reads at a glance without hiding the tails.
  const start = cycleWindow.opens.time;
  const end = boundaryEnd(cycleWindow.closes);
  const zoneStart = Number.isFinite(cycleWindow.earliest?.time) && cycleWindow.earliest.time < start ? cycleWindow.earliest.time : start;
  const hardEnd = boundaryEnd(cycleWindow.hardMax);
  const zoneEnd = Number.isFinite(hardEnd) && hardEnd > end ? hardEnd : end;
  const lo = Math.min(zone.p25, zone.p75);
  const hi = Math.max(zone.p25, zone.p75);
  const { flags, claim, what, reading, inZone } = zoneWords(depth, cfg);
  const box = ({ key, from, to, fill, span: [t0, t1], text = null, tooltip }) => ({
    key,
    type: 'position',
    data: [{
      start: t0,
      end: t1,
      entry: (from + to) / 2,
      stop: from,
      target: to,
      messages: text ? [{ t: t0, v: side === 'low' ? from : to, text }] : [],
      riskColor: fill,
      rewardColor: fill,
      bubbleColor: 'rgba(15, 23, 42, 0.9)',
      textColor: 'white',
      fontSize: 11,
      pointer: false,
      padding: 6,
    }],
    tooltipFormatter: () => tooltip,
  });
  const series = [];
  // The range is clipped to what the chart already shows: the bars and this p25–p75 zone (drawn unclipped, so
  // it stretches the axis itself). Clipping to the bars alone would drop the range whenever the zone sits beyond them.
  const shownLo = Math.min(minLow, lo);
  const shownHi = Math.max(maxHigh, hi);
  const outerLo = Math.max(Math.min(zone.min, zone.max), shownLo);
  const outerHi = Math.min(Math.max(zone.min, zone.max), shownHi);
  if (outerHi > outerLo && (outerLo < lo || outerHi > hi)) {
    const clipped = Math.min(zone.min, zone.max) < shownLo || Math.max(zone.min, zone.max) > shownHi;
    series.push(box({
      key: `${cfg.key} Range`,
      from: outerLo,
      to: outerHi,
      fill: ZONE_FAINT,
      span: [zoneStart, zoneEnd],
      tooltip: `range of past moves ${fmtPct(depth.pct.min)}–${fmtPct(depth.pct.max)}: ${fmtPrice(zone.min)} – ${fmtPrice(zone.max)}`
        + `${clipped ? ' (drawn to the edge of the chart)' : ''} — ${claim}`,
    }));
  }
  // The price tails span the whole timing tail AND the outer band (DWLF-462): one price box either side of the
  // core, worded as outside the timing core so it never contradicts the outer band's own label.
  const tailTip = (where) => `${what} — ${where}, where the timing is outside its core — ${claim}`;
  if (zoneStart < start) {
    series.push(box({ key: `${cfg.key} Early`, from: lo, to: hi, fill: cfg.tail, span: [zoneStart, start], tooltip: tailTip('before the core') }));
  }
  series.push(box({
    key: cfg.key,
    from: lo,
    to: hi,
    fill: cfg.fill,
    span: [start, end],
    text: `${inZone ? 'IN ZONE · ' : ''}${side === 'low' ? 'Low' : 'High'} zone ${fmtPrice(lo)}–${fmtPrice(hi)} (n ${depth.n}${flags})`,
    tooltip: `${what}; median ${fmtPrice(zone.median)} — n ${depth.n} cycles${flags}${reading ? ` — price now: ${reading}` : ''} — ${claim}`,
  }));
  if (zoneEnd > end) {
    series.push(box({ key: `${cfg.key} Late`, from: lo, to: hi, fill: cfg.tail, span: [end, zoneEnd], tooltip: tailTip('after the core, to the hard max') }));
  }
  return series;
};

const ZONE_MISSING = {
  retired: 'its window was withdrawn',
  refused: 'the band could not be drawn for this series',
  'missing-inputs': 'the stored window lacks the inputs',
  'no-rows-on-one-side': 'the other side has no cycles yet',
  'sides-stored-under-different-spellings': 'the two sides are stored under different symbols',
  'read-failed': 'the band could not be read',
  'internal-error': 'the band failed',
};

/**
 * One clause per drawable window whose price zone is NOT drawn, and why, or null when every drawn window
 * has its zone (DWLF-416). Only when the caller asked for depth (`depthRequested`): then a window served
 * without a band says so too. "No anchor yet" is the usual reason: no opposite pivot (the high after a low
 * ruler) has confirmed and, under the API's v2 band, no running high from candles is available either.
 */
export const describeDepthZones = ({ windows, depthRequested = false, timeframeWord = 'weekly' }) => {
  if (!windows || !depthRequested) return null;
  const part = (side, w) => {
    if (!isDrawableWindow(w) || hasDrawableZone(w)) return null;
    const opposite = side === 'low' ? 'high' : 'low';
    let why;
    if (w.depth && !w.depth.anchor) {
      why = `no confirmed ${timeframeWord} ${opposite} (and no running ${opposite} from the candles) since the ruler to measure from yet`;
    } else if (w.depth) {
      why = 'no band was served';
    } else {
      why = ZONE_MISSING[w.depthUnavailable] || 'no band was served';
    }
    return `${side} price zone not drawn: ${why}`;
  };
  const parts = [part('low', windows.low), part('high', windows.high)].filter(Boolean);
  return parts.length ? `${parts.join('; ')}.` : null;
};

// ---------------------------------------------------------------------------
// PAST windows (DWLF-444): for every closed window the store holds, the window
// that was projected from its ruler for the NEXT pivot, its price zone, and a
// marker where that pivot actually formed. The stored history is re-projected
// on today's pivot chain, not as shown at the time, so misses are drawn as
// plainly as hits and every surface states the caveat (DWLF-320; the as-shown
// record is DWLF-428). Since DWLF-462 the status line tallies each side by band
// (Andy's brief), always followed by that caveat — a tally of re-projected
// history, not a measured hit rate.
// ---------------------------------------------------------------------------
// Each past window is drawn as its core (25th–75th) and outer (17th–83rd) bands, no tail, and its
// pivot's marker says which it landed in: core, outer, early (before the outer band) or late (after
// it), from the API's `outcome.band` (`before` / `after` there, early / late on the chart).
const PAST = {
  low: { code: 'CL', key: 'Past Cycle Low Window', fill: 'rgba(59, 130, 246, 0.09)', outer: 'rgba(59, 130, 246, 0.04)', zone: 'rgba(59, 130, 246, 0.13)', pivot: 'low' },
  high: { code: 'CH', key: 'Past Cycle High Window', fill: 'rgba(249, 115, 22, 0.09)', outer: 'rgba(249, 115, 22, 0.04)', zone: 'rgba(249, 115, 22, 0.13)', pivot: 'high' },
};
// Dark green in the core, light green in the outer band, orange early, red late.
const OUTCOME_COLOR = { core: '#15803d', outer: '#86efac', early: '#f97316', late: '#ef4444', unknown: '#94a3b8' };
const VERDICTS = ['core', 'outer', 'early', 'late'];
// The API's band verdict → the chart's word. The API says before/after (the outer band) because early/late
// name the window's stages there; the chart keeps the brief's early/late, which the colours carry.
const API_BAND = { core: 'core', outer: 'outer', before: 'early', after: 'late' };
const PAST_CAVEAT = 're-projected on today\'s pivot chain, not as shown at the time';

/**
 * Where the next pivot formed against a past window, read off the STORED outcome (null when the window is
 * still open). The API's `band` when it sends one (DWLF-462: core / outer / before / after the outer band,
 * read as core / outer / early / late); else the stored hit test — `landedInside` is the core — and the sign
 * of `daysFromOpen` / `daysFromClose`. Without those it is `unknown`, never guessed. `days` is the distance
 * from the core's nearer edge.
 */
export const pastWindowOutcome = (cycleWindow) => {
  const o = cycleWindow?.outcome;
  if (!o || !Number.isFinite(o.pivotTime)) return null;
  const before = Number.isFinite(o.daysFromOpen) && o.daysFromOpen < 0 ? -o.daysFromOpen : null;
  const after = Number.isFinite(o.daysFromClose) && o.daysFromClose > 0 ? o.daysFromClose : null;
  // Own keys only: an inherited name ('toString') is not a verdict.
  const bandVerdict = Object.prototype.hasOwnProperty.call(API_BAND, o.band) ? API_BAND[o.band] : null;
  if (bandVerdict) {
    // `days` from the core's nearer edge; `side` only when a day count says which side.
    const days = bandVerdict === 'core' ? 0 : (before ?? after);
    const side = before === null ? (after === null ? null : 'after') : 'before';
    return { verdict: bandVerdict, days, side, banded: true };
  }
  // No band verdict from the API (a refused band, or an API from before DWLF-462): the stored hit test, so
  // early / late here mean outside the CORE. Marked `banded: false`, and the tally does not mix the two.
  if (o.landedInside === true) return { verdict: 'core', days: 0, banded: false };
  if (before !== null) return { verdict: 'early', days: before, banded: false };
  if (after !== null) return { verdict: 'late', days: after, banded: false };
  return { verdict: 'unknown', days: null, banded: false };
};

/** A past window the layer draws: not retired, drawable edges, and an outcome. ONE rule for drawing and counting. */
export const isDrawnPastWindow = (w) => !w?.retired && isDrawableWindow(w) && pastWindowOutcome(w) !== null;

/**
 * "lows, last 12: core 5 · outer 3 · early 2 · late 2": one side's drawn windows by band verdict, in the
 * markers' order. Only when every drawn window carries the API's band verdict; otherwise the plain count
 * ("lows, last 12"), so two definitions of early/late are never added together.
 */
const sideTally = (side, windows) => {
  const drawn = windows.filter(isDrawnPastWindow);
  if (!drawn.length) return `${side}s: none closed`;
  const outcomes = drawn.map(pastWindowOutcome);
  if (!outcomes.every((o) => o.banded)) return `${side}s, last ${drawn.length}`;
  const counts = outcomes.reduce((acc, o) => { acc[o.verdict] = (acc[o.verdict] || 0) + 1; return acc; }, {});
  const parts = VERDICTS.filter((v) => counts[v]).map((v) => `${v} ${counts[v]}`);
  return `${side}s, last ${drawn.length}: ${parts.join(' · ')}`;
};

/**
 * The status clause for the Past Windows layer while it is shown: the tally per side (where each window's
 * pivot landed), the caveat, always (the colours carry a verdict at a glance, so the chart says what history
 * they judge), plus why nothing is drawn when nothing is.
 */
export const describePastWindows = ({ shown, storeTimeframe, history, loading, error }) => {
  // `shown`: { low, high } — the sides whose past windows are on.
  const sides = ['low', 'high'].filter((side) => shown?.[side]);
  if (!sides.length) return null;
  if (!storeTimeframe) return 'Past windows: the store holds no hourly windows — nothing is drawn.';
  if (loading) return null;
  if (error) return `Past windows unavailable (${error}) — nothing is drawn in their place.`;
  const lists = sides.map((side) => [side, history?.[side] ?? []]);
  if (lists.every(([, list]) => !list.some(isDrawnPastWindow))) return `Past windows: no closed ${sides.join(' or ')} windows yet for this symbol.`;
  return `Past windows — ${lists.map(([side, list]) => sideTally(side, list)).join('; ')} — ${PAST_CAVEAT}; misses are drawn as plainly as hits.`;
};

// Banded verdicts name the edge they are judged against (the outer band) and the one the days count from (the
// core); the stored-hit-test fallback (no `outcome.band`) is early / late of the window itself, as before.
const VERDICT_WORDS = {
  core: () => 'inside the core band',
  outer: (o) => (o.days === null || o.side === null ? 'in the outer band' : `in the outer band, ${o.days} days ${o.side} the core`),
  early: (o) => {
    if (!o.banded) { return o.days === null ? 'early' : `early by ${o.days} days`; }
    return o.days === null ? 'early, before the outer band' : `early, before the outer band (${o.days} days before the core)`;
  },
  late: (o) => {
    if (!o.banded) { return o.days === null ? 'late' : `late by ${o.days} days`; }
    return o.days === null ? 'late, after the outer band' : `late, after the outer band (${o.days} days after the core)`;
  },
  unknown: () => 'where it fell against the window is not recorded',
};

/**
 * Series entries for one side's PAST windows: per closed window a faint core box and, where the API sent
 * one, its lighter outer band (no tail), its faint price zone when the band has one, and one marker series
 * per verdict placing each window's actual next pivot. Retired windows and windows without an outcome are
 * skipped.
 */
export const buildPastCycleWindows = ({ history, side, minLow, maxHigh }) => {
  const cfg = PAST[side];
  if (!cfg || !Array.isArray(history)) return [];
  if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) return [];
  const series = [];
  const points = [];
  history.filter(isDrawnPastWindow).forEach((w) => {
    const outcome = pastWindowOutcome(w);
    const ruler = w.ruler?.pivotDate || ISO(w.ruler?.pivotTime);
    const landed = w.outcome.pivotDate || ISO(w.outcome.pivotTime);
    const verdict = VERDICT_WORDS[outcome.verdict](outcome);
    const span = `${w.opens?.date || ISO(w.opens.time)} → ${w.closes?.date || ISO(boundaryEnd(w.closes))}`;
    // The outer band only beside a verdict judged against it: a core-only verdict inside an outer box would
    // read as a contradiction (a red 'late' in the light-green band).
    const outer = outcome.banded ? outerBandExtent(w) : null;
    const box = ({ key, from = w.opens.time, to = boundaryEnd(w.closes), lo, hi, fill, tooltip, fullHeight = false }) => ({
      key,
      type: 'position',
      data: [{
        start: from, end: to, entry: (lo + hi) / 2, stop: lo, target: hi, fullHeight,
        messages: [], riskColor: fill, rewardColor: fill, pointer: false, padding: 6,
        bubbleColor: 'rgba(15, 23, 42, 0.9)', textColor: 'white', fontSize: 11,
      }],
      tooltipFormatter: () => tooltip,
    });
    const outerWords = outer
      ? `; outer band (${outer.words}) ${(outer.opens ?? w.opens).date || ISO(outer.start)} → ${(outer.closes ?? w.closes).date || ISO(outer.end)}`
      : '';
    const tooltip = `past ${cfg.code} window from the ${ruler} ${cfg.pivot}: ${span}${outerWords}; the next ${cfg.pivot} formed ${landed}, ${verdict} (${PAST_CAVEAT})`;
    if (outer?.opens) {
      series.push(box({ key: `${cfg.key} Outer Early ${ruler}`, from: outer.start, to: w.opens.time, lo: minLow, hi: maxHigh, fill: cfg.outer, fullHeight: true, tooltip }));
    }
    series.push(box({ key: `${cfg.key} ${ruler}`, lo: minLow, hi: maxHigh, fill: cfg.fill, fullHeight: true, tooltip }));
    if (outer?.closes) {
      series.push(box({ key: `${cfg.key} Outer Late ${ruler}`, from: boundaryEnd(w.closes), to: outer.end, lo: minLow, hi: maxHigh, fill: cfg.outer, fullHeight: true, tooltip }));
    }
    if (hasDrawableZone(w)) {
      const lo = Math.max(Math.min(w.depth.zone.p25, w.depth.zone.p75), minLow);
      const hi = Math.min(Math.max(w.depth.zone.p25, w.depth.zone.p75), maxHigh);
      if (hi > lo) {
        series.push(box({
          key: `${cfg.key} Zone ${ruler}`, lo, hi, fill: cfg.zone,
          tooltip: `past price zone ${fmtPrice(w.depth.zone.p25)}–${fmtPrice(w.depth.zone.p75)}; the next ${cfg.pivot} formed at `
            + `${fmtPrice(w.outcome.price)} (${PAST_CAVEAT})`,
        }));
      }
    }
    if (Number.isFinite(w.outcome.price)) {
      // No `label`: the chart draws a marker's label as text. The sentence is the tooltip's alone.
      points.push({ t: w.outcome.pivotTime, v: w.outcome.price, verdict: outcome.verdict, __pastOutcome: `next ${cfg.pivot} ${landed}: ${verdict}` });
    }
  });
  // One marker series per verdict: the chart treats series-level marker style as authoritative, so a
  // single series could not colour a miss differently from a hit.
  Object.keys(OUTCOME_COLOR).forEach((v) => {
    const data = points.filter((p) => p.verdict === v);
    if (!data.length) return;
    series.push({
      key: `${cfg.key} Outcomes (${v})`,
      type: 'marker',
      data,
      style: { color: OUTCOME_COLOR[v], markerShape: 'circle', markerSize: 5, markerOffsetY: side === 'low' ? 10 : -10 },
      tooltipFormatter: (raw) => `${raw?.__pastOutcome || ''} (${PAST_CAVEAT})`,
    });
  });
  return series;
};

// One frozen empty list, so an emptied chart prop keeps its identity between renders.
const EMPTY_LIST = Object.freeze([]);

/**
 * The chart's six cycle props, from the backend hook and nowhere else — a
 * null-guarded pass-through, so the page has one place that says what the
 * chart is given. Pure, so that guarantee is tested.
 */
export const chartCycleProps = ({ backendCycles }) => {
  if (!backendCycles) {
    return {
      weeklyCycleLows: EMPTY_LIST, weeklyCycleHighs: EMPTY_LIST,
      liveCycleLowEvents: EMPTY_LIST, liveCycleHighEvents: EMPTY_LIST,
      liveCycleLow: null, liveCycleHigh: null,
    };
  }
  return {
    weeklyCycleLows: backendCycles.confirmedLows ?? EMPTY_LIST,
    weeklyCycleHighs: backendCycles.confirmedHighs ?? EMPTY_LIST,
    liveCycleLowEvents: backendCycles.liveLowEvents ?? EMPTY_LIST,
    liveCycleHighEvents: backendCycles.liveHighEvents ?? EMPTY_LIST,
    liveCycleLow: backendCycles.liveLowState ?? null,
    liveCycleHigh: backendCycles.liveHighState ?? null,
  };
};

/**
 * The whole account of what the chart is NOT drawing for cycles, and why —
 * pivots and windows together (DWLF-113 ②b). Nothing is computed in place
 * of anything missing. Pure, so the sentence is tested.
 *   pivots.error      → a failed fetch is a failed fetch, never "no data"
 *   pivots empty      → "no cycle data yet", PER SIDE (a one-sided ledger is named)
 *   tuning            → the ledger's pivots are shown; the sliders preview nothing
 *   windows (weekly)  → describeCycleWindows below
 */
export const describeCycleStatus = ({ isTuningCycles, symbol, pivots, windows, storeTimeframe, depthRequested = false, timeframeWord }) => {
  const parts = [];
  if (isTuningCycles) {
    parts.push('Tune: the chart shows the ledger\'s pivots — the sliders preview nothing here; saved settings apply on the next nightly run.');
  }
  if (pivots && !pivots.loading) {
    if (pivots.error) {
      parts.push(`Cycle pivots unavailable (${pivots.error}) — nothing is drawn in their place.`);
    } else {
      const missing = [pivots.emptyLow ? 'lows' : null, pivots.emptyHigh ? 'highs' : null].filter(Boolean);
      if (missing.length === 2) {
        parts.push(`No cycle data yet for ${symbol || 'this symbol'} on this timeframe — pivots come from the ledger, and nothing is computed in the browser.`);
      } else if (missing.length === 1) {
        parts.push(`No cycle ${missing[0]} yet for ${symbol || 'this symbol'} on this timeframe — the ledger holds none, and nothing is computed in the browser.`);
      }
    }
  }
  if (storeTimeframe && windows && !windows.loading) {
    const line = describeCycleWindows({ windows: windows.windows, error: windows.error });
    if (line) parts.push(line);
    const zones = windows.error ? null : describeDepthZones({ windows: windows.windows, depthRequested, timeframeWord });
    if (zones) parts.push(`${zones.charAt(0).toUpperCase()}${zones.slice(1)}`);
  }
  return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * One line for the page — ONLY when something is not drawn (an error, a side
 * with no stored window, or a stored window the chart cannot draw). When both
 * bands are on the chart the shading is the information and the line stays
 * out of the plot's way. "Drawn" is decided by the same predicate the band
 * builder uses, so the line can never fall silent on a band that is missing.
 */
export const describeCycleWindows = ({ windows, error }) => {
  if (error) return `Cycle windows unavailable (${error}) — nothing is drawn in their place.`;
  if (!windows) return null;
  if (isDrawableWindow(windows.low) && isDrawableWindow(windows.high)) return null;
  const part = (side, w) => {
    if (!w) return `${side}: no stored window yet`;
    if (!isDrawableWindow(w)) return `${side}: stored window has no drawable edges — nothing is drawn in its place`;
    const calib = w.calibration === 'uncalibrated' ? ', uncalibrated' : '';
    const n = Number.isFinite(w.gaps?.n) ? `, n ${w.gaps.n}` : '';
    return `${side}: ${w.opens?.date || ISO(w.opens?.time)} → ${w.closes?.date || ISO(boundaryEnd(w.closes))}, hard max ${w.hardMax?.date || ISO(boundaryEnd(w.hardMax))}${n}${calib}`;
  };
  const verif = windows.verification?.state || 'unverified';
  return `Cycle windows (${verif}) — ${part('low', windows.low)}; ${part('high', windows.high)}.`;
};

// ---------------------------------------------------------------------------
// The band's timeframe bucket — ONE rule for the page's raw key and the
// chart's key, so the two can never disagree about which band applies: a key
// naming hours is hourly, one naming weeks is weekly, anything else is daily.
// The store covers weekly and daily (DWLF-113 ①, DWLF-271); hourly keeps the
// legacy state band.
// ---------------------------------------------------------------------------
export const bandTimeframe = (timeframeKey) => {
  const key = typeof timeframeKey === 'string' ? timeframeKey.toLowerCase() : '';
  if (key.includes('hour') || key === '1h') return 'hourly';
  if (key.includes('week') || key === '1w') return 'weekly';
  return 'daily';
};
export const isStoreTimeframe = (timeframeKey) => typeof timeframeKey === 'string'
  && timeframeKey !== '' && bandTimeframe(timeframeKey) !== 'hourly';

// ---------------------------------------------------------------------------
// HOURLY ONLY — the pre-②a band, extracted behaviour-for-behaviour from the
// chart: the state row's q25..q75
// (in bars) when it has ≥5 samples, else mean ± 25 % of the gaps between the
// visible pivots. The low side's "overdue" is suppressed by a live pivot
// inside the band; the high side's never was.
// ---------------------------------------------------------------------------
export const buildLegacyStateBand = ({ pivots, stateSide, side, unitMs, unitLabel, now, minLow, maxHigh, livePivotTime }) => {
  if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) return [];
  const q = stateSide?.quantiles;
  const lastConfirmed = stateSide?.lastConfirmedPivotTime;
  const useBackendQuantiles = !!q && Number.isFinite(lastConfirmed)
    && Number.isFinite(q.q25) && Number.isFinite(q.q50) && Number.isFinite(q.q75)
    && q.q75 > q.q25 && (q.samplesUsed ?? 0) >= 5;
  let windowStart;
  let windowEnd;
  let avgUnits;
  if (useBackendQuantiles) {
    windowStart = lastConfirmed + q.q25 * unitMs;
    windowEnd = lastConfirmed + q.q75 * unitMs;
    avgUnits = q.q50;
  } else {
    if (!Array.isArray(pivots) || pivots.length < 3) return [];
    const intervals = [];
    for (let i = 1; i < pivots.length; i += 1) {
      const tPrev = pivots[i - 1]?.pivotTime;
      const tCurr = pivots[i]?.pivotTime;
      if (Number.isFinite(tPrev) && Number.isFinite(tCurr) && tCurr > tPrev) intervals.push(tCurr - tPrev);
    }
    if (!intervals.length) return [];
    const meanMs = intervals.reduce((sum, value) => sum + value, 0) / intervals.length;
    avgUnits = meanMs / unitMs;
    const tolUnits = Math.max(Math.round(avgUnits * 0.25), 4);
    const lastTime = pivots[pivots.length - 1]?.pivotTime;
    if (!Number.isFinite(lastTime)) return [];
    windowStart = lastTime + (avgUnits - tolUnits) * unitMs;
    windowEnd = lastTime + (avgUnits + tolUnits) * unitMs;
  }
  if (!Number.isFinite(windowStart) || !Number.isFinite(windowEnd) || windowEnd <= windowStart) return [];
  const isLow = side === 'low';
  const hasLive = isLow && Number.isFinite(livePivotTime) && livePivotTime >= windowStart && livePivotTime <= windowEnd;
  const overdue = now > windowEnd && !hasLive;
  const code = isLow ? 'CL' : 'CH';
  const entry = (minLow + maxHigh) / 2;
  const hint = useBackendQuantiles ? `q50 ~${Math.round(avgUnits)}${unitLabel}` : `avg ~${Math.round(avgUnits)}${unitLabel}`;
  const label = `${overdue ? 'Overdue' : 'Next'} ${code} window (${hint})`;
  const baseFill = isLow ? 'rgba(59, 130, 246, 0.18)' : 'rgba(249, 115, 22, 0.18)';
  return [{
    key: isLow ? 'Next Cycle Low Window' : 'Next Cycle High Window',
    type: 'position',
    data: [{
      start: windowStart,
      end: windowEnd,
      entry,
      stop: minLow,
      target: maxHigh,
      fullHeight: true,
      messages: [{ t: windowStart, v: isLow ? minLow : maxHigh, text: label }],
      rewardColor: overdue ? LATE_FILL : baseFill,
      bubbleColor: 'rgba(15, 23, 42, 0.9)',
      textColor: 'white',
      fontSize: 11,
      pointer: true,
      padding: 6,
    }],
    tooltipFormatter: (datum) => `${ISO(datum?.start)} → ${ISO(datum?.end)}`,
  }];
};
